import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import { getProjectDetailRequest } from "store/project/reducer";
import {
  selectedProjectSelector,
  isFetchingDetails,
  unreadMessageSelector
} from "store/project/selector";
import { fetchUnreadMessagesCnt, pad_with_zeroes, fetchStatusName } from "network/helper";
import { getProgressByDocuments } from "views/customer/project-detail/constants/statusProjects";
import { SelectedProject, ProjectMetaItem } from "views/customer/project-detail/types";
import { userSelector } from "store/user/selector";
import { axiosService } from "network/axios";
import { isEmpty, uniqBy } from "lodash";

interface UseProjectDetailReturn {
  selectedProject: SelectedProject | null;
  documents: any[];
  progressPercentage: number;
  loading: boolean;
  error: string;
  refreshProject: () => void;
  projectMeta: ProjectMetaItem[];
  projectManagers: string[];
  projectManagerIds: number[];
  trackingDetails: any;
  unreadMessageCnt: number;
  showUpload: boolean;
}

export const useProjectDetail = (): UseProjectDetailReturn => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const selectedProject = useSelector(selectedProjectSelector) as SelectedProject | null;
  const loading = useSelector(isFetchingDetails) as boolean;
  const unreadMessages = useSelector(unreadMessageSelector);
  const user = useSelector(userSelector);
  const [trackingDetails, setTrackingDetails] = useState(null);

  const documents = useMemo(() => {
    const { translated_from, translated_to } = selectedProject?.order || {};
    const documents = selectedProject?.documents || [];
    return documents?.map((it: any) => {
      return {
        language: null,
        source_lang: translated_from,
        target_lang: translated_to,
        ...it,
      };
    }) || [];
  }, [selectedProject]);

  const unreadMessageCnt = useMemo(() => {
    if (id && unreadMessages) {
      return fetchUnreadMessagesCnt(unreadMessages, Number(id));
    } else {
      return 0;
    }
  }, [unreadMessages, id]);

  const projectMeta = useMemo((): ProjectMetaItem[] => {
    const order = selectedProject?.order;
    if (!order) return [];

    const isEnabled = (flag?: string): boolean => flag === '1';
    const formatBoolean = (condition: boolean): 'Yes' | 'No' => condition ? 'Yes' : 'No';
    const formatDate = (dateStr?: string): string | null =>
      dateStr ? new Date(dateStr).toLocaleDateString() : null;
    const formatPrice = (amount?: number): string => `$${amount || 0}`;

    const translationTypeLabel =
      order.translation_type === 'certified'
        ? 'certificate'
        : order.translation_type || 'Undefined';

    const isCertified = order.translation_type === 'certified';
    const isStandard = order.translation_type === 'standard';

    const metaConfig: Array<{
      label: string;
      value: string | number | null;
      showWhen?: boolean;
    }> = [
        {
          label: 'Order Date',
          value: formatDate(order.created_at),
        },
        {
          label: 'Pages',
          value: Number(order.page_count) || 0,
          showWhen: !isStandard,
        },
        {
          label: 'Words',
          value: Number(order.words_count) || 0,
          showWhen: !isCertified,
        },
        {
          label: 'Type',
          value: translationTypeLabel,
        },
        {
          label: 'Notarized',
          value: formatBoolean(isEnabled(order.include_notarization)),
          showWhen: isCertified,
        },
        {
          label: 'Two-Day Shipping Delivery',
          value: formatBoolean(
            isEnabled(order.include_2_day_shipping) || isEnabled(order.include_mail_hard_copy)
          ),
          showWhen: isCertified,
        },
        {
          label: 'Expedite Translation',
          value: formatBoolean(isEnabled(order.include_expedited_turnaround)),
        },
        {
          label: 'Price',
          value: formatPrice(order.order_total),
        },
        {
          label: 'Address',
          value: !isEmpty(order.address_fields) ? order.address_fields : null,
        },
      ];

    return metaConfig.reduce<ProjectMetaItem[]>((acc, { label, value, showWhen }) => {
      if (showWhen !== false && value !== null) {
        acc.push({ label, value: value! });
      }
      return acc;
    }, []);
  }, [selectedProject]);

  const projectManagers = useMemo(() => {
    return (
      uniqBy(selectedProject?.projectManager, "user_id")?.map((it: any) => {
        return `${it.first_name} ${it.last_name}`;
      }) || []
    );
  }, [selectedProject]);

  const projectManagerIds = useMemo(() => {
    return (
      uniqBy(selectedProject?.projectManager, "user_id")?.map((it: any) => {
        return Number(it.user_id);
      }) || []
    );
  }, [selectedProject]);

  const showUpload = useMemo(() => {
    const fileNames = documents.map((document: any) => {
      return document?.origin_file?.document_original_file_name;
    }).filter((documentName: string) => {
      return documentName && documentName.length > 0;
    });
    return fileNames.length < 1;
  }, [documents]);

  const fetchTrackingNumber = useCallback(async () => {
    if (!id) return;

    try {
      const resp: any = await axiosService.get(
        `/shipping/tracking/${id}`
      );
      const trackingLink = resp?.data?.tracking_link || null;
      const receiptLink = resp?.data?.receipt_link || [];
      const trackingNumber = resp?.data?.tracking_number || null;
      const shippingStatus =
        resp?.data?.shipping_status ||
        resp?.data?.shippingStatus ||
        resp?.data?.status ||
        null;

      setTrackingDetails({
        trackingLink,
        receiptLink,
        trackingNumber,
        shippingStatus,
      });
    } catch (err: any) {
      console.log(err);
    }
  }, [id]);

  useEffect(() => {
    if (selectedProject && id && user) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "project_page_opened",
        project_id: pad_with_zeroes(id, 6),
        project_status: fetchStatusName(
          Number(selectedProject.order?.order_status_id)
        ),
        project_due_date: selectedProject.order?.order_due_at
          ? new Date(selectedProject.order.order_due_at).toLocaleDateString()
          : "-",
        project_price: Number(selectedProject.order?.order_total),
        user_id: user.user_id,
      });
    }
  }, [selectedProject, id, user]);

  useEffect(() => {
    if (id) {
      fetchTrackingNumber();
    }
  }, [id, fetchTrackingNumber]);

  const progressPercentage = useMemo(
    () => getProgressByDocuments(documents),
    [documents]
  );

  const refreshProject = useCallback(() => {
    if (id && dispatch) {
      dispatch(getProjectDetailRequest({ orderId: id }));
      fetchTrackingNumber();
    }
  }, [id, dispatch, fetchTrackingNumber]);

  useEffect(() => {
    refreshProject();
  }, [refreshProject]);

  return {
    selectedProject,
    documents,
    progressPercentage,
    loading,
    error: "", // TODO: Add error selector when available
    refreshProject,
    projectMeta,
    projectManagers,
    projectManagerIds,
    trackingDetails,
    unreadMessageCnt,
    showUpload,
  };
};
